import {
  evaluateFieldValidation,
  resolveString
} from '@mobilelive-inc/telus-forms-core';

/**
 * SOF-3402/SOF-3439, Stage 4c-iii. `<FormRunner>`'s built-in validation
 * (`engine.goNext()`) only ever validates `engine.steps[engine.currentStepIndex]`
 * — meaningless once the engine's `definition` spans every step (4c-ii) but
 * Vulcan's own "current step" is external/URL-driven with business-rule
 * step-skip logic (`step + 2` for BusinessConnect orders) the engine's
 * internal `goNext`/`goPrev` (±1 only) can't express, so `currentStepIndex`
 * is never advanced.
 *
 * This is a direct, faithful port of the package's own internal
 * `validateStepFields` (`src/engine/v2/useFormRunner.js`, not exported) —
 * same shape, same fallback message — built entirely on the package's
 * already-exported `evaluateFieldValidation`/`resolveString`, over WHICHEVER
 * step's fields the caller supplies (Vulcan's real current step, not the
 * engine's unused internal one).
 *
 * @param {Array<{name: string}>} stepFields  Leaf fields for ONE step (e.g. `collectLeafFields(currentStep.items)`)
 * @param {Object.<string, Object>} fieldStates  `engine.fieldStates` — whole-order, keyed by field name
 * @param {string} locale
 * @param {Object} [strings]
 * @returns {Object.<string, string>} `{ [fieldName]: errorMessage }`
 */
const DEFAULT_REQUIRED_MESSAGE = 'This field is required';

export function validateVulcanStepFields(
  stepFields,
  fieldStates,
  locale,
  strings
) {
  const errors = {};
  (stepFields || []).forEach(field => {
    const state = fieldStates[field.name];
    if (!state) return;
    const result = evaluateFieldValidation(field, state.value, state.isVisible);
    if (!result) return;
    if (result.kind === 'required') {
      // A choice field with no options left renders "Not available"; nothing can be selected.
      if (
        ['MultiCheckbox', 'Radio', 'Select'].includes(state.type) &&
        Array.isArray(state.options) &&
        state.options.length === 0
      ) {
        return;
      }
      const msg = resolveString('validation.required', locale, strings);
      errors[field.name] =
        msg === 'validation.required' ? DEFAULT_REQUIRED_MESSAGE : msg;
    } else {
      errors[field.name] = result.message;
    }
  });
  return errors;
}

export default validateVulcanStepFields;
